import withOver, { appAlert } from "../lib/withOver";
import { asMarionetteView } from "lib/common/hoc/BackboneReact/asMarionetteView";
import MappingModalModule from "lib/mapping/MappingModalModule";
import MergeModalResolver from "lib/merge/MergeModalResolver";
import { BackboneAboutModule as BackboneAboutModal } from "lib/about/AboutModule";
import { BackbonePreferencesModule as BackbonePreferencesModal } from "lib/preferences/PreferencesModule";
import ValidationFilterModal from "lib/validationFilter/ValidationFilterModal";
import HiddenFieldAdminModal from "lib/hiddenFields/HiddenFieldAdminModal";
import Dispatcher from "Dispatcher";
import NavigatorStore from "data/NavigatorStore";
import {
    superNavTree,
    TOP_LEVEL_RECORDS_KEY,
} from "data/selectors/superRecords";
import TableStore from "data/TableStore";
import SuperRecordStore from "data/SuperRecordStore";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import MapButtonActions from "data/MapButtonActions";
import MergeActions from "data/MergeActions";
import WidgetActions from "data/WidgetActions";
import { BackboneGenericXmlExportPasswordAdmin } from "lib/genericXmlExportPassword/GXEPModule";
import { BackboneBroadcastAdmin } from "lib/broadcast/BroadcastAdminModule";
import { BackboneTestReauth } from "lib/reauth/ReauthModule";
import implementationFieldsByEntity
    from "data/selectors/implementationFieldsByEntity";
import userStore from "data/userStore";
import UserActions from "data/UserActions";
import TwoPaneMultiSelectActions
    from "lib/common/twopanemultiselect/data/TwoPaneMultiSelectActions";
import PrintModule from "lib/dataentry/lib/print/PrintModule";
import PrintActions from "lib/dataentry/lib/print/PrintActions";
import DataManager from "data/DataManager";
import { ACTION_CONTEXT_KEY } from "data/util/ContextedReduceStore";
import contextFromAppModelOrContext from "./util/contextFromAppModelOrContext";

const MarionetteAboutModal = asMarionetteView(BackboneAboutModal);
const MarionettePreferencesModal = asMarionetteView(BackbonePreferencesModal);
const MarionetteValidationFilterModal = asMarionetteView(ValidationFilterModal);
const MarionetteHiddenFieldAdminModal = asMarionetteView(HiddenFieldAdminModal);
const MarionetteMappingModalModule = asMarionetteView(MappingModalModule);
const MarionetteMergeModalResolver = asMarionetteView(MergeModalResolver);
const MarionetteGenericXmlPasswordAdmin = asMarionetteView(BackboneGenericXmlExportPasswordAdmin);
const MarionettePrintDialog = asMarionetteView(PrintModule);
const MarionetteBroadcastAdmin = asMarionetteView(BackboneBroadcastAdmin);
const MarionetteTestReauth = asMarionetteView(BackboneTestReauth);

const findTargetId = (tree, record, config) => {
    let targetId = null;
    // see if it's the current record
    if (record.key === config.entity) {
        targetId = record.id;
    }
    const find = filter => {
        const queue = [];
        if (tree != null) queue.push(tree);
        while (queue.length > 0) {
            const item = queue.shift();
            if (item.entityKey === config.entity) {
                return item.id;
            }
            if (!item.fields) continue;
            for (const f of item.fields) {
                if (!f.items) continue;
                const it = f.items.find(filter);
                if (it) queue.push(it);
            }
        }
    };
    // see if it's an ancestor
    if (targetId == null) {
        targetId = find(it => it.selected);
    }
    // look in the entire tree
    if (targetId == null) {
        targetId = find(() => true);
    }
    return targetId;
};

const addPeer = (appModelOrFluxContext, tree, activeRecord, config, dispatch) => {
    // find me in the tree...
    const queue = [];
    if (tree != null) queue.push(...tree.fields);
    while (queue.length > 0) {
        const field = queue.shift();
        if (!field.items) continue;
        if (field.creatables.some(it => it.key === config.entity) && field.items.some(it => it.id === activeRecord.id)) {
            dispatch(
                "record:new-child",
                TableStore.getEntityProxy(field.recordKey, field.recordId),
                field.key,
                config.entity,
                {shortname: config.field},
                appModelOrFluxContext,
            );
            return true;
        }
        for (const it of field.items) {
            if (!it.fields) continue;
            queue.push(...it.fields);
        }
    }
    return false;
};

class ViewController extends Backbone.Controller {

    initialize(options){
        this.model = options.model;
        this.app = options.app;
        Dispatcher.register(action => {
            switch (action.type) {
                case PrintActions.LAUNCH_DIALOG:
                    this.app.layout.showModal(() =>
                        new MarionettePrintDialog(action));
                    break;
                case MapButtonActions.LAUNCH_DIALOG:
                    // MappingModule causes a secondary dispatch to load
                    // the mapping plugins search forms with selected stereotype
                    // and default values. This setTimeout gets us past that.
                    //
                    // eslint-disable-next-line no-settimeout
                    setTimeout(() => {
                        this.app.layout.showModal(() =>
                            new MarionetteMappingModalModule({
                                fluxContext: action[ACTION_CONTEXT_KEY],
                            }));
                    });
                    break;
                case WidgetActions.WIDGET_CLICK:
                    this.onClickWidgetButton(action.config, action[ACTION_CONTEXT_KEY]);
                    break;
                case MergeActions.LAUNCH_DIALOG:
                    this.onShowMergeModal(action[ACTION_CONTEXT_KEY]);
                    break;
                default:
            }
        });
    }

    onShowMergeModal(appModelOrFluxContext) {
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        this.app.layout.showModal(() =>
            new MarionetteMergeModalResolver({
                fluxContext: fluxContext,
            }));
    }

    onAbout() {
        this.app.layout.showModal(() =>
            new MarionetteAboutModal());
    }

    onPreferences() {
        this.app.layout.showModal(() =>
            new MarionettePreferencesModal());
    }

    onGenericXmlPasswordAdmin() {
        this.app.layout.showModal(() =>
            new MarionetteGenericXmlPasswordAdmin());
    }

    onBroadcastAdmin() {
        this.app.layout.showModal(() =>
            new MarionetteBroadcastAdmin());
    }

    onTestReauth() {
        this.app.layout.showModal(() =>
            new MarionetteTestReauth());
    }

    _doFieldAdmin(org, implKey, groupsBuilder, pref, Modal) {
        withOver(
            "Loading...",
            () => DataManager.promiseImplementationAwareness(org, implKey)
                .then(() => {
                    TwoPaneMultiSelectActions.populate(
                        groupsBuilder(),
                        JSON.parse(userStore.getLicensePreference(pref, implKey)),
                    );
                    this.app.layout.showModal(() => new Modal({
                        triggerSave: selection => Dispatcher.dispatch({
                            type: UserActions.UPDATE_LICENSE_PREFERENCE,
                            impl: implKey,
                            pref,
                            value: JSON.stringify(selection),
                        }),
                    }));
                }),
        );
    }

    onLoadValidationFilter(org, implKey) {
        this._doFieldAdmin(
            org,
            implKey,
            () =>
                implementationFieldsByEntity(
                    implKey,
                    fi =>
                        fi.dqr),
            "validation-excluded-fields",
            MarionetteValidationFilterModal
        );
    }

    onLoadHiddenFieldAdmin(org, implKey) {
        this._doFieldAdmin(
            org,
            implKey,
            () =>
                implementationFieldsByEntity(
                    implKey,
                    fi =>
                        fi.userHideable,
                    true),
            "user-hidden-fields",
            MarionetteHiddenFieldAdminModal
        );
    }

    onClickWidgetButton(config, fluxContext) {
        if (config.command === "continue") {
            this.onContinue(config, false, fluxContext);
        } else if (config.command === "create") {
            this.onContinue(config, true, fluxContext);
        } else if (process.env.NODE_ENV !== "production") {
            console.warn("bad button widget config", config);
        }
    }

    onContinue(config, forceCreate, appModelOrFluxContext) {
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        const navState = NavigatorStore.getState(fluxContext);
        const {magic, record} = navState;
        const tree = superNavTree(
            TableStore,
            SuperRecordStore.getRecord(
                magic.key,
                magic.id,
                fluxContext,
            ),
            navState,
            magic.key,
            magic.id,
        )
        // but only this TLR's tree...
            .fields
            .find(it => it.key === TOP_LEVEL_RECORDS_KEY)
            .items
            .find(it => it.selected);
        let targetId;
        if (!forceCreate) {
            targetId = findTargetId(tree, record, config);
        }
        if (targetId != null) {
            // this key passing is stupid, but that's how it works
            this.dispatch(
                "validation:load",
                config.entity,
                targetId,
                config.field,
                appModelOrFluxContext,
            );
            return;
        }
        // ok, find a place to create one...

        const activeRecord = TableStore.getEntityProxy(record.key, record.id);

        // if i have a collection of the right type, add one there
        for (const fk of activeRecord.entityInfo.associations.cmv) {
            const fieldInfo = entityFieldMetaStore.getInfo(record.key, fk);
            if (fieldInfo.otherEntityKey === config.entity) {
                this.dispatch(
                    "record:new-child",
                    activeRecord,
                    fk,
                    config.entity,
                    {shortname: config.field},
                    appModelOrFluxContext,
                );
                return;
            }
        }

        // if i am of the right type, add a peer
        if (activeRecord.entityKey === config.entity) {
            if(addPeer(appModelOrFluxContext, tree, activeRecord, config, this.dispatch)) {
                // successful peer location found
                return;
            }
        }

        // otherwise, bomb
        appAlert("No suitable location to create record.");
    }

}

ViewController.prototype.events = {
    "load:about": "onAbout",
    "load:preferences": "onPreferences",
    "load:genericXmlPasswordAdmin": "onGenericXmlPasswordAdmin",
    "load:broadcastAdmin": "onBroadcastAdmin",
    "load:test-reauth": "onTestReauth",
    "load:validation_filter": "onLoadValidationFilter",
    "load:hidden_field_admin": "onLoadHiddenFieldAdmin",
};

export default ViewController;



// WEBPACK FOOTER //
// ./app/controllers/ViewController.js